import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { SHIFT_PRIORITIES, type ShiftPriority } from '@acs/shared'
import { cn } from '@/lib/cn'

/**
 * The shift a customer wants, as a picture: a gold sun for days, a violet
 * moon for nights, both for either. Shared by the table row, the phone card
 * and the drawer's picker, so the three cannot disagree about which shape —
 * or which colour — means what.
 *
 * ── Drawn here, not borrowed ────────────────────────────────────────
 * Lucide's icons are 2px outlines on a 24px grid, which is right for a
 * toolbar and wrong for this: at 18px a stroked sun is a gear and a stroked
 * moon is a bracket. These two are drawn as SHAPES — a disc that is lit from
 * its upper left, a crescent that deepens toward its edge, each in a soft
 * halo of its own colour, and two small stars in the moon's cutout — so they
 * read as a sun and a moon from across the room, on a white ground or a
 * black one, with no chip behind them.
 *
 * Every colour is a mix of the two theme tokens, `--sun` and `--moon`, so
 * the highlight, the body and the halo all follow the theme: a gold sun on
 * the light ground, a clear yellow one on the dark. The tokens are defined
 * in theme.css a deliberate step away from warn and from the accent, and
 * appear nowhere else.
 *
 * ── The gradient ids ────────────────────────────────────────────────
 * An SVG gradient is referenced by document-wide id, and a virtualised list
 * mounts hundreds of these. `useId` gives each instance its own, so a row
 * unmounting can never take another row's gradient with it. The value is
 * squeezed to `[A-Za-z0-9_-]` because React wraps it in delimiters that a
 * `url(#…)` fragment does not care for.
 */

export const SHIFT_PRIORITY_LABEL: Readonly<Record<ShiftPriority, string>> = Object.freeze({
  Any: 'Any shift',
  'Day Shift': 'Day shift',
  'Night Shift': 'Night shift',
})

/** The one-word form the picker's buttons carry beside their glyph. */
const SHORT_LABEL: Readonly<Record<ShiftPriority, string>> = Object.freeze({
  Any: 'Any',
  'Day Shift': 'Day',
  'Night Shift': 'Night',
})

function useGradientId(): string {
  const raw = React.useId()
  return `shift-${raw.replace(/[^A-Za-z0-9_-]/g, '')}`
}

/** The lit side of a shape: its token, pushed most of the way toward white. */
const highlight = (token: '--sun' | '--moon', amount: number): string =>
  `color-mix(in srgb, var(${token}) ${amount}%, white)`

/**
 * A sun: eight round-capped rays around a disc that is lit from its upper
 * left, in a halo that fades to nothing before the box edge.
 */
function SunGlyph() {
  const id = useGradientId()
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="text-sun">
      <defs>
        <radialGradient id={`${id}-disc`} cx="38%" cy="34%" r="70%">
          <stop offset="0" style={{ stopColor: highlight('--sun', 42) }} />
          <stop offset="0.55" style={{ stopColor: highlight('--sun', 82) }} />
          <stop offset="1" style={{ stopColor: 'var(--sun)' }} />
        </radialGradient>
        <radialGradient id={`${id}-halo`}>
          <stop offset="0" style={{ stopColor: 'var(--sun)', stopOpacity: 0.34 }} />
          <stop offset="0.6" style={{ stopColor: 'var(--sun)', stopOpacity: 0.1 }} />
          <stop offset="1" style={{ stopColor: 'var(--sun)', stopOpacity: 0 }} />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="11.5" fill={`url(#${id}-halo)`} />
      <path
        d="M12 1.6v2.9M12 19.5v2.9M1.6 12h2.9M19.5 12h2.9M4.65 4.65l2.05 2.05M17.3 17.3l2.05 2.05M4.65 19.35l2.05-2.05M17.3 6.7l2.05-2.05"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="4.9" fill={`url(#${id}-disc)`} />
    </svg>
  )
}

/**
 * A moon: a crescent lit along its inner edge — the side the bite is on,
 * where the sun would be — and deepening toward its outer curve, two small
 * stars where the sky shows through, and its own halo.
 *
 * Deep on the OUTSIDE on purpose. The outer curve is the whole silhouette,
 * and a pale one melts into a white ground; lit from the bite, the shape
 * keeps a saturated edge on both themes and the highlight lands where the
 * horns are thinnest, which is where a crescent actually catches light.
 */
function MoonGlyph() {
  const id = useGradientId()
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="text-moon">
      <defs>
        <linearGradient id={`${id}-body`} x1="0.9" y1="0.1" x2="0.15" y2="0.95">
          <stop offset="0" style={{ stopColor: highlight('--moon', 58) }} />
          <stop offset="0.45" style={{ stopColor: highlight('--moon', 86) }} />
          <stop offset="1" style={{ stopColor: 'var(--moon)' }} />
        </linearGradient>
        <radialGradient id={`${id}-halo`}>
          <stop offset="0" style={{ stopColor: 'var(--moon)', stopOpacity: 0.3 }} />
          <stop offset="0.6" style={{ stopColor: 'var(--moon)', stopOpacity: 0.09 }} />
          <stop offset="1" style={{ stopColor: 'var(--moon)', stopOpacity: 0 }} />
        </radialGradient>
      </defs>
      <circle cx="10.6" cy="13.4" r="10.6" fill={`url(#${id}-halo)`} />
      {/* The crescent: a 9-radius disc with a 6-radius bite out of its upper right. */}
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill={`url(#${id}-body)`} />
      {/* A four-point star and a fainter one, in the sky the bite left behind. */}
      <path
        d="M18.6 2.4l.72 1.78 1.78.72-1.78.72-.72 1.78-.72-1.78-1.78-.72 1.78-.72z"
        fill="currentColor"
        opacity="0.92"
      />
      <path
        d="M21.6 8.3l.42 1.02 1.02.42-1.02.42-.42 1.02-.42-1.02-1.02-.42 1.02-.42z"
        fill="currentColor"
        opacity="0.6"
      />
    </svg>
  )
}

function Glyphs(p: { value: ShiftPriority }) {
  switch (p.value) {
    case 'Day Shift':
      return <SunGlyph />
    case 'Night Shift':
      return <MoonGlyph />
    case 'Any':
      return (
        <>
          <SunGlyph />
          <MoonGlyph />
        </>
      )
  }
}

/**
 * The row's marker, beside the customer id: the glyph alone, no chip, at
 * 18px — large enough that the disc and the crescent are shapes rather than
 * dots, and still inside the line the virtualised row already budgets for.
 * Nothing at all for a customer with no priority on file: an empty space is
 * the honest rendering, and a dash would be one more character on six hundred
 * rows.
 *
 * Primitive props only, so the default shallow memo holds while the run map
 * republishes frames around it.
 */
export const ShiftPriorityIcons = React.memo(function ShiftPriorityIcons(p: {
  value: ShiftPriority | null
  /** `card` is a step larger, for the phone layout's looser line. */
  size?: 'row' | 'card'
  className?: string
}) {
  if (p.value === null) return null
  const label = SHIFT_PRIORITY_LABEL[p.value]
  const card = p.size === 'card'
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex flex-none items-center [&_svg]:shrink-0',
        card ? 'gap-1 [&_svg]:size-5' : 'gap-0.5 [&_svg]:size-[18px]',
        p.className
      )}
    >
      <Glyphs value={p.value} />
    </span>
  )
})

/**
 * Three pills, one pressed. The same control as the create form's, sized for
 * a drawer cell: picking replaces, re-clicking keeps. The glyphs keep their
 * own colours whether or not the pill is pressed — the colour says WHICH
 * shift, the accent ring says which one is chosen, and the two never compete.
 *
 * `saving` names the pick in flight, so the button the operator just pressed
 * shows the spinner while the rest sit disabled — a second tap mid-flight
 * would otherwise race two writes and could leave the row showing the loser.
 */
export function ShiftPriorityPicker(p: {
  value: ShiftPriority | null
  saving: ShiftPriority | null
  onPick: (next: ShiftPriority) => void
  /** Names the group for assistive tech; the visible label is the caller's. */
  'aria-label': string
}) {
  const busy = p.saving !== null
  return (
    <span role="group" aria-label={p['aria-label']} className="inline-flex flex-wrap gap-1">
      {SHIFT_PRIORITIES.map((option) => {
        const on = p.value === option
        const spinning = p.saving === option
        return (
          <button
            key={option}
            type="button"
            aria-pressed={on}
            aria-busy={spinning || undefined}
            disabled={busy}
            title={SHIFT_PRIORITY_LABEL[option]}
            onClick={() => p.onPick(option)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px] font-medium',
              'transition-colors duration-100 [&_svg]:size-4 [&_svg]:shrink-0',
              'disabled:cursor-not-allowed disabled:opacity-60',
              on
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-line text-muted hover:border-line-strong hover:text-fg'
            )}
          >
            {spinning ? (
              <Loader2 aria-hidden="true" className="animate-spin" />
            ) : (
              <span className="inline-flex items-center gap-0.5">
                <Glyphs value={option} />
              </span>
            )}
            {SHORT_LABEL[option]}
          </button>
        )
      })}
    </span>
  )
}
